import {
  EmergencyCall,
  EmergencyLocation,
  EmergencyCallSecondaryLocation,
  EmergencyCallLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { isEmergencyCallLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';

/**
 * Function that returns the most recent call object
 * @param call callQueue[callerID]
 */
export const getMostRecentLoc = (
  call?: EmergencyCall,
): EmergencyLocation | EmergencyCallSecondaryLocation | null => {
  // TODO: passing in an object {} will always pass the condition and cause unexpected errors. add tests
  if (call && call.core) {
    const { locations, secondaryLocations } = call.core;
    const mostRecentLocIdx = locations?.length - 1;
    const mostRecentSecondaryLocIdx = secondaryLocations?.length - 1;
    if (
      locations &&
      locations.length &&
      secondaryLocations &&
      secondaryLocations.length
    ) {
      const mostRecent = locations[mostRecentLocIdx];
      if ('call_start_time' in mostRecent) {
        return mostRecent.call_start_time >
          secondaryLocations[mostRecentSecondaryLocIdx].call_start_time
          ? locations[mostRecentLocIdx]
          : secondaryLocations[mostRecentSecondaryLocIdx];
      } else {
        return mostRecent.conversation_start_time >
          secondaryLocations[mostRecentSecondaryLocIdx].call_start_time
          ? locations[mostRecentLocIdx]
          : secondaryLocations[mostRecentSecondaryLocIdx];
      }
    }

    return locations?.[mostRecentLocIdx]
      ? locations?.[mostRecentLocIdx]
      : secondaryLocations?.[mostRecentSecondaryLocIdx]
        ? secondaryLocations?.[mostRecentSecondaryLocIdx]
        : null;
  }
  return null;
};

interface MostRecentLocations {
  mostRecentLocation: EmergencyLocation | null;
  mostRecentLocationIndex: number | null;
  mostRecentSecondaryLocation: EmergencyCallSecondaryLocation | null;
  mostRecentSecondaryLocationIndex: number | null;
}

export const getMostRecentLocations = (call?: EmergencyCall) => {
  const locs: MostRecentLocations = {
    mostRecentLocation: null,
    mostRecentLocationIndex: null,
    mostRecentSecondaryLocation: null,
    mostRecentSecondaryLocationIndex: null,
  };
  // TODO: passing in an object {} will always pass the condition and cause unexpected errors. add tests
  if (call) {
    const { locations, secondaryLocations } = call.core || {};
    const mostRecentLocIdx = locations?.length - 1;
    const mostRecentSecondaryLocIdx = secondaryLocations?.length - 1;

    if (locations && locations.length) {
      locs.mostRecentLocation = locations[mostRecentLocIdx];
      locs.mostRecentLocationIndex = mostRecentLocIdx;
    }
    if (secondaryLocations && secondaryLocations.length) {
      locs.mostRecentSecondaryLocation =
        secondaryLocations[mostRecentSecondaryLocIdx];
      locs.mostRecentSecondaryLocationIndex = mostRecentSecondaryLocIdx;
    }
  }
  return locs;
};

/**
 * Get the most recent location (primary or secondary) with an actual location.
 *
 * Not all objects in the `locations` area have latitude and longitude. This
 * function find the most recent item with a location.
 */
export const getMostRecentLocationWithLocation = (
  call?: EmergencyCall,
): EmergencyCallLocation | EmergencyCallSecondaryLocation | undefined => {
  if (!call) return undefined;
  let mostRecentLocation: EmergencyCallLocation | undefined = undefined;
  let mostRecentSecondaryLocation: EmergencyCallSecondaryLocation | undefined =
    undefined;

  if (call.core.locations) {
    for (let index = call.core.locations.length - 1; index >= 0; index--) {
      const location = call.core.locations[index];

      if (isEmergencyCallLocation(location)) {
        mostRecentLocation = location;
        break;
      }
    }
  }

  if (call.core.secondaryLocations && call.core.secondaryLocations.length) {
    mostRecentSecondaryLocation =
      call.core.secondaryLocations[call.core.secondaryLocations.length - 1];
  }

  if (mostRecentLocation && mostRecentSecondaryLocation) {
    return mostRecentLocation.location_time >
      mostRecentSecondaryLocation.location_time
      ? mostRecentLocation
      : mostRecentSecondaryLocation;
  }

  if (mostRecentLocation && !mostRecentSecondaryLocation) {
    return mostRecentLocation;
  }

  if (!mostRecentLocation && mostRecentSecondaryLocation) {
    return mostRecentSecondaryLocation;
  }

  return undefined;
};
